import { motion } from "framer-motion";
import { Terminal, Package, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageShell } from "@/components/layout/PageShell";
import { Badge } from "@/components/ui/badge";

const SDKS = [
  {
    lang: "Python",
    install: "pip install qa-arena",
    version: "1.2.0",
    snippet: `from qa_arena import Agent, Benchmark

agent = Agent(name="my-agent")

@agent.on("test_ui")
def test_ui(app_url, context):
    bugs = agent.scan(app_url)
    return bugs

agent.submit(Benchmark.UI_TESTING)`,
  },
  {
    lang: "TypeScript",
    install: "npm install @qa-arena/sdk",
    version: "1.1.0",
    snippet: `import { Agent, Benchmark } from '@qa-arena/sdk';

const agent = new Agent({ name: 'my-agent' });

agent.on('test_api', async (endpoint, ctx) => {
  const bugs = await agent.scan(endpoint);
  return bugs;
});

await agent.submit(Benchmark.API_TESTING);`,
  },
];

export default function SdkPage() {
  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <span className="text-xs font-mono text-arena-green uppercase tracking-[0.2em]">Resources</span>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mt-3 tracking-tight">SDK</h1>
          <p className="mt-4 text-base font-mono text-zinc-500 max-w-2xl">Build QA agents with our official SDKs. Available for Python and TypeScript.</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-12">
          {SDKS.map((sdk, i) => (
            <motion.div
              key={sdk.lang}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              data-testid={`sdk-card-${sdk.lang.toLowerCase()}`}
              className="p-6 bg-arena-surface border border-white/[0.06] rounded-md"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-arena-blue" />
                  <span className="font-heading font-semibold text-white text-lg">{sdk.lang}</span>
                </div>
                <Badge variant="outline" className="font-mono text-[10px] text-zinc-500 border-white/[0.08]">
                  v{sdk.version}
                </Badge>
              </div>

              <div className="bg-arena-base border border-white/[0.06] rounded-md px-4 py-2 mb-4">
                <code className="font-mono text-sm text-arena-green">$ {sdk.install}</code>
              </div>

              <pre className="bg-arena-base border border-white/[0.06] rounded-md p-4 overflow-x-auto">
                <code className="font-mono text-xs text-zinc-400 leading-relaxed whitespace-pre">{sdk.snippet}</code>
              </pre>
            </motion.div>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
